import React, { useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import EventModal from "./EventModal";
import EventDetail from './EventDetail';
import api from "../services/api";
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import '../styles/Settings.css';

import frLocale from '@fullcalendar/core/locales/fr';
import enLocale from '@fullcalendar/core/locales/en-gb';
import esLocale from '@fullcalendar/core/locales/es';
import deLocale from '@fullcalendar/core/locales/de';
import arLocale from '@fullcalendar/core/locales/ar';
import ptLocale from '@fullcalendar/core/locales/pt';
import itLocale from '@fullcalendar/core/locales/it';
import daLocale from '@fullcalendar/core/locales/da';
import nbLocale from '@fullcalendar/core/locales/nb';
import fiLocale from '@fullcalendar/core/locales/fi';
import svLocale from '@fullcalendar/core/locales/sv';
import ukLocale from '@fullcalendar/core/locales/uk';
import huLocale from '@fullcalendar/core/locales/hu';
import isLocale from '@fullcalendar/core/locales/is';
import elLocale from '@fullcalendar/core/locales/el';
import roLocale from '@fullcalendar/core/locales/ro';
import bgLocale from '@fullcalendar/core/locales/bg';
import srLocale from '@fullcalendar/core/locales/sr';
import slLocale from '@fullcalendar/core/locales/sl';
import etLocale from '@fullcalendar/core/locales/et';
import lvLocale from '@fullcalendar/core/locales/lv';
import ltLocale from '@fullcalendar/core/locales/lt';
import hrLocale from '@fullcalendar/core/locales/hr';
import csLocale from '@fullcalendar/core/locales/cs';
import skLocale from '@fullcalendar/core/locales/sk';
import nlLocale from '@fullcalendar/core/locales/nl';
import plLocale from '@fullcalendar/core/locales/pl';

import {
  getGroupKeyForDiscussion,
  getCachedGroupKey,
  encryptEventFields,
  decryptEventFields,
} from "../services/signalGroupCrypto";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
const token = localStorage.getItem("token");

const Icon = ({ d, size = 17, color = 'currentColor', style = {} }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    fill="none" stroke={color} strokeWidth="1.75"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ display: 'block', flexShrink: 0, pointerEvents: 'none', ...style }}
    aria-hidden="true"
  >
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const iconRedStyle = {
  fontSize: '22px',
  color: 'red',
};
const iconBlueStyle = {
  fontSize: '18px',
  color: '#007bff',
};

const ICONS = {
  infoCircle:  ['M12 9h.01', 'M11 12h1v4h1', 'M12 3a9 9 0 1 0 0 18A9 9 0 0 0 12 3'],
}

// ── Utilitaire dateKey LOCAL (évite le décalage UTC) ──────────────
const toDateKey = (date) =>
  `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;

// ── Séparateur unique pour les ids virtuels ───────────────────────
const SEP = '--';

// ── generateOccurrences ───────────────────────────────────────────
const generateOccurrences = (masterEvent, rangeStart, rangeEnd) => {
  const recurrence = masterEvent.recurrence || masterEvent.extendedProps?.recurrence;
  let { freq, interval = 1, endDate, daysOfWeek = [] } = recurrence || {};
  if (!freq || freq === 'none') return [masterEvent];

  // weekly sans jours cochés → utiliser le jour de départ
  if (freq === 'weekly' && daysOfWeek.length === 0) {
    daysOfWeek = [new Date(masterEvent.start).getDay()];
  }

  const results = [];
  const masterStart = new Date(masterEvent.start);
  const masterEnd   = new Date(masterEvent.end);
  const duration    = masterEnd - masterStart;
  const limit = endDate
    ? new Date(endDate)
    : new Date(masterStart.getFullYear() + 2, 0, 1);
  const maxOccurrences = 500;
  let count = 0;

  // ── Hebdomadaire ────────────────────────────────────────────────
  if (freq === 'weekly') {
    let current = new Date(masterStart);
    // Reculer au dimanche de la semaine de départ (local)
    current.setDate(current.getDate() - current.getDay());
    current.setHours(masterStart.getHours(), masterStart.getMinutes(), 0, 0);
    let weekCount = 0;

    while (current <= limit && count < maxOccurrences) {
      if (weekCount % interval === 0) {
        for (let d = 0; d < 7; d++) {
          const day = new Date(current);
          day.setDate(current.getDate() + d);

          if (!daysOfWeek.includes(day.getDay())) continue;
          if (day < masterStart) continue;
          if (day > limit) break;

          const inRange =
            (!rangeStart || day >= new Date(rangeStart)) &&
            (!rangeEnd   || day <= new Date(rangeEnd));

          if (inRange) {
            const dateKey = toDateKey(day);
            const virtId  = `${masterEvent.id}${SEP}${dateKey}`;
            results.push({
              ...masterEvent,
              id:    virtId,
              _id:   virtId,
              start: new Date(day).toISOString(),
              end:   new Date(day.getTime() + duration).toISOString(),
              isVirtual: true,
              extendedProps: {
                ...masterEvent.extendedProps,
                recurrence,
                isVirtual: true,
              },
            });
            count++;
          }
        }
      }
      current.setDate(current.getDate() + 7);
      weekCount++;
    }

  // ── Daily / Monthly ─────────────────────────────────────────────
  } else {
    let current = new Date(masterStart);

    while (current <= limit && count < maxOccurrences) {
      const inRange =
        (!rangeStart || current >= new Date(rangeStart)) &&
        (!rangeEnd   || current <= new Date(rangeEnd));

      if (inRange) {
        const dateKey = toDateKey(current);
        const virtId  = `${masterEvent.id}${SEP}${dateKey}`;
        results.push({
          ...masterEvent,
          id:    virtId,
          _id:   virtId,
          start: new Date(current).toISOString(),
          end:   new Date(current.getTime() + duration).toISOString(),
          isVirtual: true,
          extendedProps: {
            ...masterEvent.extendedProps,
            recurrence,
            isVirtual: true,
          },
        });
        count++;
      }

      if (freq === 'daily')        current.setDate(current.getDate() + interval);
      else if (freq === 'monthly') current.setMonth(current.getMonth() + interval);
    }
  }

  return results;
};

// ── Helpers id ────────────────────────────────────────────────────
const getRealId         = (id) => id.split(SEP)[0];
const isVirtualId       = (id) => id.includes(SEP);
const startsWithMaster  = (id, masterId) => id === masterId || id.startsWith(`${masterId}${SEP}`);

const Agenda = ({ discussionId, discussionName, discussionCreatedBy, userId, socket, prompt, onClose, whiteboardList, onOpenWhiteboard, highlightEventId, setHighlightEventId }) => {
  const [events, setEvents]           = useState([]);
  const [showModal, setShowModal]     = useState(false);
  const [showDetail, setShowDetail]   = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate]   = useState(null);
  const calendarRef    = useRef(null);
  const visibleRangeRef = useRef({ start: null, end: null });
const discussionRef = useRef(null);
const groupKeyRef = useRef(null);
  const { i18n, t }    = useTranslation();

  const [agendaNotifEnabled, setAgendaNotifEnabled] = useState(true);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

const decryptAndFormatEvent = async (event) => {
const currentDiscussion = discussionRef.current;
  const isE2EEDiscussion = currentDiscussion &&
    !currentDiscussion.isPublic &&
    currentDiscussion.groupKeyWraps?.length > 0;
if (!isE2EEDiscussion) {
    return formatEvent({
      ...event,
      title: typeof event.title === 'string' ? event.title : '',
      description: typeof event.description === 'string' ? event.description : '',
    });
  }
  let groupKeyRaw = groupKeyRef.current;


  // Si la clé n'est pas encore disponible,
  // récupérer la discussion puis obtenir la clé.
  if (!groupKeyRaw) {
    try {
      let currentDiscussion = discussionRef.current;

      if (!currentDiscussion) {
        const discussionResponse = await api.get(
          `${API_URL}/api/discussions/${discussionId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        currentDiscussion = discussionResponse.data;
        discussionRef.current = currentDiscussion;
      }

      groupKeyRaw = await getGroupKeyForDiscussion(
        currentDiscussion,
	socket,
        userId
      );

      groupKeyRef.current = groupKeyRaw;
    } catch (err) {
      console.error(
        '❌ Impossible de récupérer la clé de groupe pour l’agenda :',
        discussionId,
        err
      );
    }
  }

  const decrypted = await decryptEventFields(
    event,
    groupKeyRaw
  );

  return formatEvent({
    ...event,
    title: decrypted.title,
    description: decrypted.description,
  });
};

  // ── Charger les événements ──────────────────────────────────────
  const loadEvents = async (start, end) => {
  try {
    // ─────────────────────────────────────────────
    // 1. Récupérer la discussion
    // ─────────────────────────────────────────────
    const discussionResponse = await api.get(
      `${API_URL}/api/discussions/${discussionId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const currentDiscussion = discussionResponse.data;

    // Conserver la discussion pour les prochains déchiffrements
    discussionRef.current = currentDiscussion;

    // ─────────────────────────────────────────────
    // 2. Récupérer la clé de groupe
    // ─────────────────────────────────────────────
    try {
      groupKeyRef.current = await getGroupKeyForDiscussion(
        currentDiscussion,
	socket,
        userId
      );
    } catch (err) {
      console.error(
        '❌ Impossible de récupérer la clé de groupe pour l’agenda :',
        discussionId,
        err
      );

      groupKeyRef.current = null;
    }

    // ─────────────────────────────────────────────
    // 3. Charger les événements
    // ─────────────────────────────────────────────
    const params = new URLSearchParams();

    if (start) {
      params.append(
        'start',
        start instanceof Date ? start.toISOString() : start
      );
    }

    if (end) {
      params.append(
        'end',
        end instanceof Date ? end.toISOString() : end
      );
    }

    const response = await api.get(
      `${API_URL}/api/events/${discussionId}?${params}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // ─────────────────────────────────────────────
    // 4. Déchiffrer les événements
    // ─────────────────────────────────────────────
    const formattedEvents = await Promise.all(
      response.data.map(event =>
        decryptAndFormatEvent(event)
      )
    );

    setEvents(formattedEvents);

  } catch (error) {
    console.error("Erreur chargement événements :", error.response?.data || error);
  }
};

  // ── Notifs agenda ───────────────────────────────────────────────
  useEffect(() => {
  const loadAgendaNotif = async () => {
    try {
      const res = await api.get(
        `${API_URL}/api/discussions/${discussionId}/agenda-notifications`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAgendaNotifEnabled(res.data.agenda);
    } catch (err) { console.error(err); }
  };
  if (discussionId) loadAgendaNotif();
}, [discussionId]);

  // ── Ouvrir depuis notification ──────────────────────────────────
  useEffect(() => {
    if (!highlightEventId || events.length === 0) return;
    const event = events.find(e => String(e.id) === String(highlightEventId));
    if (!event) return;
    setSelectedEvent({
      id:             event.id,
      title:          event.title.replace('🔒 ', ''),
      start:          event.start,
      end:            event.end,
      allDay:         event.allDay,
      color:          event.extendedProps.color || event.backgroundColor,
      description:    event.extendedProps.description,
      whiteboards:    event.extendedProps.whiteboards,
      visibility:     event.extendedProps.visibility,
      reminderMinutes: event.extendedProps.reminderMinutes,
      createdBy:      event.extendedProps.createdBy,
      creatorName:    event.extendedProps.creatorName,
    });
    setShowDetail(true);
    const calApi = calendarRef.current?.getApi();
    if (calApi && event.start) {
      calApi.gotoDate(event.start);
      calApi.changeView('timeGridDay');
      setTimeout(() => setHighlightEventId(null), 300);
    }
  }, [highlightEventId, events]);

  // ── formatEvent ─────────────────────────────────────────────────
  const formatEvent = (e) => {
    const color     = e.color || e.backgroundColor || '#3788d8';
    const isPrivate = e.visibility === 'private';
    const isOwn     = e.createdBy === userId;
    const eventId   = e._id || e.id;
    return {
      id:              eventId,
      title:           (isPrivate && isOwn) ? `🔒 ${e.title}` : e.title,
      start:           e.start,
      end:             e.end,
      allDay:          e.allDay,
      backgroundColor: isPrivate ? '#888' : color,
      borderColor:     isPrivate ? '#555' : color,
      extendedProps: {
        description:     e.description,
        createdBy:       e.createdBy,
        creatorName:     e.creatorName || e.createdBy,
        whiteboards:     e.whiteboards || [],
        visibility:      e.visibility,
        reminderMinutes: e.reminderMinutes,
        color,
        recurrence:      e.recurrence || null,
        isVirtual:       e.isVirtual  || false,
      },
    };
  };

  // ── Clic date → création ────────────────────────────────────────
  const handleDateClick = (info) => {
    setSelectedEvent(null);
    setSelectedDate(info.dateStr);
    setShowDetail(false);
    setShowModal(true);
  };

  // ── Clic événement → lecture ────────────────────────────────────
  const handleEventClick = (info) => {
    setSelectedEvent({
      id:              info.event.id,
      title:           info.event.title.replace('🔒 ', ''),
      start:           info.event.startStr,
      end:             info.event.endStr,
      allDay:          info.event.allDay,
      color:           info.event.extendedProps.color || info.event.backgroundColor,
      description:     info.event.extendedProps.description,
      whiteboards:     info.event.extendedProps.whiteboards,
      visibility:      info.event.extendedProps.visibility,
      reminderMinutes: info.event.extendedProps.reminderMinutes,
      createdBy:       info.event.extendedProps.createdBy,
      creatorName:     info.event.extendedProps.creatorName,
      recurrence:      info.event.extendedProps.recurrence,
      isVirtual:       info.event.extendedProps.isVirtual,
    });
    setShowDetail(true);
    setShowModal(false);
  };

  // ── Drag & drop ─────────────────────────────────────────────────
  const handleEventDrop = async (info) => {
    try {
      const response = await api.put(
        `${API_URL}/api/events/${info.event.id}`,
        { start: info.event.start, end: info.event.end || info.event.start },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      socket.emit("event-updated", { discussionId, event: formatEvent(response.data) });
    } catch (error) {
      console.error("Erreur déplacement événement :", error);
      info.revert();
    }
  };

  // ── handleSave ──────────────────────────────────────────────────
  const handleSave = async (eventData) => {
    try {
const isE2EEDiscussion = discussionRef.current &&
  !discussionRef.current.isPublic &&
  discussionRef.current.groupKeyWraps?.length > 0;

const groupKeyRaw = groupKeyRef.current;

      // ── PUT ──────────────────────────────────────────────────────
      if (eventData.id) {
        const mode       = eventData.mode || 'all';
        const realId     = getRealId(eventData.id);
        const replacedId = eventData.id; // id virtuel complet ex: abc123--20260521
	

if (isE2EEDiscussion && !groupKeyRaw) {
  throw new Error("Clé de groupe introuvable : impossible de chiffrer l'événement.");
}

const encryptedFields = isE2EEDiscussion
  ? await encryptEventFields(eventData.title, eventData.description, groupKeyRaw)
  : { title: eventData.title, description: eventData.description }; 


const payload = {
  ...eventData,
  ...encryptedFields,
  discussionId,
  createdBy: userId,
};

	const response = await api.put(
  `${API_URL}/api/events/${realId}`,
  payload
);
        

        const formatted = await decryptAndFormatEvent(response.data);
        const recurrence = formatted.extendedProps?.recurrence || response.data.recurrence;

        // Occurrences régénérées (pour mode all / following)
        const occurrences =
          recurrence?.freq && recurrence.freq !== 'none'
            ? generateOccurrences(
                { ...formatted, recurrence },
                visibleRangeRef.current.start,
                visibleRangeRef.current.end
              )
            : [formatted];

        setEvents((prev) => {
          if (mode === 'all') {
            return [
              ...prev.filter(e => !startsWithMaster(e.id, realId)),
              ...occurrences,
            ];
          }

          if (mode === 'this') {
            // Garder l'id virtuel : l'exception prend la place de l'occurrence
            const formattedWithId = {
              ...formatted,
              id:  replacedId,
              _id: replacedId,
              extendedProps: {
                ...formatted.extendedProps,
                recurrence: { ...recurrence, isException: true },
              },
            };
            return [
              ...prev.filter(e => e.id !== replacedId),
              formattedWithId,
            ];
          }

          if (mode === 'following') {
            const occurrenceDateStr = isVirtualId(eventData.id) ? eventData.id.split(SEP)[1] : null;
            const filtered = prev.filter(e => {
              if (!startsWithMaster(e.id, realId)) return true;
              if (!isVirtualId(e.id)) return true;
              const dateStr = e.id.split(SEP)[1];
              return occurrenceDateStr ? dateStr < occurrenceDateStr : true;
            });
            return [...filtered, ...occurrences];
          }

          // Fallback — événement simple non récurrent
          return prev.map(e => e.id === formatted.id ? formatted : e);
        });

        // Socket
        if (eventData.visibility === 'shared') {
          const wasPrivate = selectedEvent?.visibility === 'private';
          socket.emit(wasPrivate ? "event-created" : "event-updated", {
            discussionId,
            event: {
              ...response.data,
              mode,
              realId,
              replacedId, // ← les autres clients savent quel id virtuel remplacer
            },
          });
        } else if (selectedEvent?.visibility === 'shared') {
          socket.emit("event-deleted", { discussionId, eventId: replacedId, mode, realId });
        }

      // ── POST ─────────────────────────────────────────────────────
      } else {
if (isE2EEDiscussion && !groupKeyRaw) {
  throw new Error("Clé de groupe introuvable : impossible de chiffrer l'événement.");
}

const encryptedFields = isE2EEDiscussion
  ? await encryptEventFields(eventData.title, eventData.description, groupKeyRaw)
  : { title: eventData.title, description: eventData.description }; 


const payload = {
  ...eventData,
  ...encryptedFields,
  discussionId,
  createdBy: userId,
};

	const response = await api.post(
  `${API_URL}/api/events`,
  payload
);

        
        const formatted = await decryptAndFormatEvent(response.data);
        const recurrence = formatted.extendedProps?.recurrence || response.data.recurrence;
        const occurrences =
          recurrence?.freq && recurrence.freq !== 'none'
            ? generateOccurrences(
                { ...formatted, recurrence },
                visibleRangeRef.current.start,
                visibleRangeRef.current.end
              )
            : [formatted];

        setEvents((prev) => [...prev, ...occurrences]);

        if (eventData.visibility === 'shared') {
          socket.emit("event-created", { discussionId, event: response.data });
        }
      }

      setShowModal(false);
    } catch (error) {
      console.error("Erreur sauvegarde événement :", error.response?.data || error);
    }
  };

  // ── handleDelete ────────────────────────────────────────────────
  const handleDelete = async (id, mode = 'single') => {
    const confirmed = await prompt.confirm(t('agenda.confirmDelete'));
    if (!confirmed) return;

    try {
      const realId           = getRealId(id);
      const occurrenceDateStr = isVirtualId(id) ? id.split(SEP)[1] : null;

      await api.delete(
        `${API_URL}/api/events/${realId}?mode=${mode}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setEvents((prev) => {
        if (mode === 'all')    return prev.filter(e => !startsWithMaster(e.id, realId));
        if (mode === 'this')   return prev.filter(e => e.id !== id);
        if (mode === 'following') {
          return prev.filter(e => {
            if (!startsWithMaster(e.id, realId)) return true;
            if (!isVirtualId(e.id)) return true;
            return occurrenceDateStr ? e.id.split(SEP)[1] < occurrenceDateStr : true;
          });
        }
        return prev.filter(e => e.id !== id); // single
      });

      socket.emit("event-deleted", { discussionId, eventId: id, mode, realId });
      setShowModal(false);
      setShowDetail(false);
    } catch (error) {
      console.error("Erreur suppression événement :", error);
    }
  };

const handleClearAgenda = async () => {
  // Première confirmation
  const confirmed1 = await prompt.confirm(t('agenda.clear_confirm1'));
  if (!confirmed1) return;

  // Deuxième confirmation — plus explicite
  const confirmed2 = await prompt.confirm(t('agenda.clear_confirm2'));
  if (!confirmed2) return;

  try {
    await api.delete(
      `${API_URL}/api/events/discussion/${discussionId}?userId=${userId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setEvents([]);
    socket.emit("agenda-cleared", { discussionId });
    showToast(t('agenda.clear_success'), 'success');
  } catch (error) {
    if (error.response?.status === 403) {
      prompt.alert(t('agenda.clear_forbidden'));
    } else {
      console.error("Erreur nettoyage agenda :", error);
    }
  }
};

  // ── Sockets ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!socket) return;
    loadEvents();
    socket.emit("join-agenda", { discussionId, userId });

    socket.on("event-created", async (event) => {
  try {
    const formatted = await decryptAndFormatEvent(event);
      const recurrence = formatted.extendedProps?.recurrence || event.recurrence;
      const occurrences =
        recurrence?.freq && recurrence.freq !== 'none'
          ? generateOccurrences(
              { ...formatted, recurrence },
              visibleRangeRef.current.start,
              visibleRangeRef.current.end
            )
          : [formatted];
      setEvents((prev) => {
        const newIds = new Set(occurrences.map(o => o.id));
        return [...prev.filter(e => !newIds.has(e.id)), ...occurrences];
      });
} catch (err) {
    console.error(
      "Erreur déchiffrement événement reçu par socket :",
      err
    );
  }
    });

    socket.on("event-updated", async (event) => {
  try {
    const formatted = await decryptAndFormatEvent(event);
      const mode       = event.mode || 'all';
      const realId     = event.realId || getRealId(event._id || event.id);
      const replacedId = event.replacedId;
     
      const recurrence = formatted.extendedProps?.recurrence || event.recurrence;

      const occurrences =
        recurrence?.freq && recurrence.freq !== 'none'
          ? generateOccurrences(
              { ...formatted, recurrence },
              visibleRangeRef.current.start,
              visibleRangeRef.current.end
            )
          : [formatted];

      setEvents((prev) => {
        if (mode === 'all') {
          return [
            ...prev.filter(e => !startsWithMaster(e.id, realId)),
            ...occurrences,
          ];
        }
        if (mode === 'this') {
          const formattedWithId = {
            ...formatted,
            id:  replacedId,
            _id: replacedId,
          };
          return [
            ...prev.filter(e => e.id !== replacedId),
            formattedWithId,
          ];
        }
        if (mode === 'following') {
      const cutDateStr = replacedId && isVirtualId(replacedId)
        ? replacedId.split(SEP)[1]
        : null;

      if (!cutDateStr) {
        // Pas de date de coupure → recharger proprement
        loadEvents(visibleRangeRef.current.start, visibleRangeRef.current.end);
        return prev;
      }

      // 1. Retirer les occurrences de l'ancienne série >= date de coupure
      const filtered = prev.filter(e => {
        if (!startsWithMaster(e.id, realId)) return true; // autre série → garder
        if (!isVirtualId(e.id)) return true;              // maître → garder
        return e.id.split(SEP)[1] < cutDateStr;           // avant coupure → garder
      });

      // 2. Ajouter les nouvelles occurrences de la série modifiée (>= coupure)
      return [...filtered, ...occurrences];
    }
        return prev.map(e => e.id === formatted.id ? formatted : e);
      });
} catch (err) {
    console.error(
      "Erreur déchiffrement événement mis à jour :",
      err
    );
  }
    });

    socket.on("event-deleted", ({ eventId, mode, realId }) => {
      const masterId = realId || getRealId(eventId);
      setEvents((prev) => {
        if (mode === 'all')      return prev.filter(e => !startsWithMaster(e.id, masterId));
        if (mode === 'this')     return prev.filter(e => e.id !== eventId);
        if (mode === 'following') {
	const cutDateStr = isVirtualId(eventId) ? eventId.split(SEP)[1] : null;
	if (!cutDateStr) {
        // Pas de date → recharger depuis le serveur
        loadEvents(visibleRangeRef.current.start, visibleRangeRef.current.end);
        return prev;
      }
          //loadEvents(visibleRangeRef.current.start, visibleRangeRef.current.end);
          //return prev;
	return prev.filter(e => {
        if (!startsWithMaster(e.id, masterId)) return true; // autre série → garder
        if (!isVirtualId(e.id)) return true;                // maître → garder
        return e.id.split(SEP)[1] < cutDateStr;             // avant coupure → garder
      });
        }
        return prev.filter(e => e.id !== eventId); // single
      });
    });
socket.on("agenda-cleared", () => {
  setEvents([]);
  showToast(t('agenda.clear_by_admin'), 'muted');
});
    return () => {
      socket.off("event-created");
      socket.off("event-updated");
      socket.off("event-deleted");
      socket.off("agenda-cleared");
    };
  }, [discussionId, socket]);

  // ── Bouton today ────────────────────────────────────────────────
  useEffect(() => {
    const todayBtn = document.querySelector('.fc-today-button');
    if (!todayBtn) return;
    const handleTodayClick = () => {
      const calApi = calendarRef.current?.getApi();
      if (!calApi) return;
      calApi.today();
      calApi.changeView('timeGridDay');
    };
    todayBtn.addEventListener('click', handleTodayClick);
    return () => todayBtn.removeEventListener('click', handleTodayClick);
  }, [showModal]);

  // ── Locales FullCalendar ────────────────────────────────────────
  const localeMap = {
    fr: frLocale, en: enLocale, es: esLocale, de: deLocale,
    ar: arLocale, pt: ptLocale, it: itLocale, nb: nbLocale,
    da: daLocale, is: isLocale, fi: fiLocale, sv: svLocale,
    uk: ukLocale, hu: huLocale, el: elLocale, ro: roLocale,
    bg: bgLocale, sr: srLocale, sl: slLocale, et: etLocale,
    lv: lvLocale, lt: ltLocale, hr: hrLocale, cs: csLocale,
    sk: skLocale, nl: nlLocale, pl: plLocale,
  };
  const currentLocale = localeMap[i18n.language] || frLocale;

  if (!socket) return <div>{t('discussion.chargement')}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '8px' }}>
      {/* Header */}
<div style={{
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 12,
  padding: '8px 4px',
  borderBottom: '1px solid var(--border-color, #eee)',
  gap: 8,
background: 'var(--card-bg)',
}}>

  {/* Gauche — icône + titre + discussion */}
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
    <span style={{ fontSize: 20, flexShrink: 0 }}>📅</span>
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-color)' }}>
          {t('agenda.titre')}
        </span>
        {/* Bulle info */}
        <span
          className="info-bubble"
          onClick={() =>
            prompt.alert(
              <span dangerouslySetInnerHTML={{ __html: t('agenda.explication_agenda') }} />
            )
          }
          style={{ cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
	aria-label="Informations"
        >
          
          <Icon d={ICONS.infoCircle} size={16} color="#888" />

        </span>
      </div>
      {/* Nom de la discussion */}
      {discussionName && (
        <span style={{
          fontSize: 12,
          color: 'var(--text-muted, #888)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'block',
          maxWidth: 160,
        }}>
          {discussionName}
        </span>
      )}
    </div>
  </div>

  {/* Centre / Droite — bouton today + fermer */}
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
    <button
      onClick={() => {
        const api = calendarRef.current?.getApi();
        if (!api) return;
        api.today();
        api.changeView('timeGridDay');
      }}
      style={{
        padding: '5px 12px',
        borderRadius: 20,
        border: '1px solid var(--border-color, #ccc)',
        background: 'none',
        cursor: 'pointer',
        fontSize: 13,
        color: 'var(--text-color)',
        whiteSpace: 'nowrap',
      }}
    >
      {t('agenda.today')}
    </button>
{/* Afficher uniquement au créateur */}
{discussionCreatedBy === userId && (
  <button
    onClick={handleClearAgenda}
    title={t('agenda.clear_title')}
    style={{
      background: 'none', border: 'none',
      cursor: 'pointer', padding: 4,
      display: 'flex', alignItems: 'center',
    }}
  >
    <i class="ti ti-clear-all" style={iconBlueStyle}></i>
  </button>
)}

    <button
      onClick={onClose}
      style={{
        background: 'none',
        border: 'none',
        fontSize: 18,
        cursor: 'pointer',
        color: '#f44336',
        lineHeight: 1,
        padding: 4,
      }}
    >
      <i className="ti ti-circle-x" style={iconRedStyle} aria-hidden="true" />
    </button>
  </div>

</div>
<div style={{
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 12,
flexWrap: 'wrap'
}}>

  <span>
    🔔 {t('agenda.notifications')}
  </span>
<label className="switch">

  <input
    type="checkbox"
    checked={agendaNotifEnabled}
    onChange={async (e) => {

      const enabled = e.target.checked;

      setAgendaNotifEnabled(enabled);
  // ← affiche le toast
  showToast(
    enabled ? t('agenda.notif_on') : t('agenda.notif_off'),
    enabled ? 'success' : 'muted'
  );

      try {

        await api.patch(
          `${API_URL}/api/discussions/${discussionId}/agenda-notifications`,
          { enabled },
      { headers: { Authorization: `Bearer ${token}` } }
        );

      } catch(err) {

        console.error(err);
      }
    }}
  />
<span className="slider2"></span>
 </label>
{/* ← toast inline */}
  {toast && (
    <span style={{
      fontSize: 12,
      color: toast.type === 'success' ? '#1D9E75' : '#888',
      padding: '3px 8px',
      borderRadius: 12,
      background: toast.type === 'success' ? '#e8f8f2' : '#f0f0f0',
      animation: 'fadeIn 0.2s ease',
      transition: 'opacity 0.3s ease',
    }}>
      {toast.type === 'success' ? '✓' : '🔕'} {toast.message}
    </span>
  )}
</div>

<style>{`
  .fc .fc-toolbar {
    flex-wrap: wrap;
    gap: 6px;
  }
  .fc .fc-toolbar-chunk {
    width: 100%;
    display: flex;
    justify-content: center;
  }
  .fc .fc-toolbar-chunk:nth-child(1) { order: 1; }
  .fc .fc-toolbar-chunk:nth-child(2) { order: 2; }
  .fc .fc-toolbar-chunk:nth-child(3) { order: 3; }
  .fc .fc-button {
    padding: 4px 8px !important;
    font-size: 13px !important;
  }
  .fc .fc-toolbar-title {
    font-size: 20px !important;
  }

  /* ← cases de jour adaptables en hauteur */
  .fc .fc-daygrid-event {
    background: none !important;
    border: none !important;
    white-space: normal !important;
    overflow: visible !important;
  }
  .fc .fc-daygrid-event-harness {
    overflow: visible !important;
  }
  . fc-list-event {
align-items: center;
}

tr th, .fc .fc-list-day{background: var(--card-bg)!important;}
`}</style>

      <FullCalendar
  ref={calendarRef}
  plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
  initialView="dayGridMonth"
  
  // ← 3 lignes de navigation
  headerToolbar={{
    left: '',
    center: 'prev,next dayGridMonth,timeGridWeek,timeGridDay,listWeek',
    right: 'title'
  }}

  // ← sur mobile, force le header sur 3 lignes
  views={{
    timeGridWeek: {
      // ← "lun 04" au lieu de "lun 04 mai"
      dayHeaderFormat: { weekday: 'short', day: '2-digit' },
    },
    dayGridMonth: {
      dayHeaderFormat: { weekday: 'short' },
    },
    timeGridDay: {
      dayHeaderFormat: { weekday: 'long', day: '2-digit', month: 'long' }, // ← format jour complet
    },
  }}

   
  locale={currentLocale}
  buttonText={{
    today: t('agenda.today'),
    month: t('agenda.month'),
    week: t('agenda.week'),
    list: t('agenda.list'),
    day: t('agenda.day'),
  }}
  events={events}
  editable={true}
  selectable={true}
  dateClick={handleDateClick}
  eventClick={handleEventClick}
  eventDrop={handleEventDrop}
  height="auto"
  timeZone="local"
  eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
  eventContent={(arg) => {
    const isPrivate = arg.event.extendedProps.visibility === 'private';
    const title = arg.event.title.replace('🔒 ', ''); // ← retire le préfixe du titre
    const bgColor = arg.event.backgroundColor;
    const borderColor = arg.event.borderColor;

    return (
      <div style={{
        position: 'relative',
        padding: '4px 4px',
        width: '100%',
        overflow: 'hidden',
	backgroundColor: bgColor,       // ← applique la couleur
      	borderLeft: `1px solid ${borderColor}`, // ← bordure gauche colorée
      	borderRadius: 3,
      	color: '#fff',  
      }}>
        {/* Cadenas en coin supérieur droit */}
        {isPrivate && (
          <span style={{
            position: 'absolute',
            top: 1,
            right: 3,
            fontSize: 10,
            lineHeight: 1,
          }}>
            🔒
          </span>
        )}

        {/* Heure si pas toute la journée */}
        {!arg.event.allDay && (
          <span style={{
            fontSize: 11,
            opacity: 0.85,
            marginRight: 4,
            whiteSpace: 'nowrap',
	    display: 'none',
          }}>
            {new Intl.DateTimeFormat(i18n.language, {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }).format(arg.event.start)}
          </span>
        )}

        {/* Titre multiligne */}
        <span style={{
          fontSize: 12,
          overflow: 'hidden',
          lineHeight: 1,
          paddingRight: isPrivate ? 14 : 0, // ← espace pour le cadenas
          display: 'block',
        }}>
          {title}
        </span>
      </div>
    );
  }}
/>

      {/* Modal création/édition */}
{/* Vue lecture */}
{showDetail && selectedEvent && (
  <EventDetail
   key={selectedEvent.id}
    userId={userId}
    event={selectedEvent}
    highlightEventId={highlightEventId}
    prompt={prompt}
    onEdit={() => {
      setShowDetail(false); // ← ferme lecture
      setShowModal(true);   // ← ouvre édition
    }}
    onDelete={handleDelete}
    onClose={() => setShowDetail(false)}
    onOpenWhiteboard={(whiteboardId) => {
      setShowDetail(false);
      onOpenWhiteboard(whiteboardId);
    }}
  />
)}
      {/* Vue édition */}
{showModal && selectedEvent && (
  <EventModal
    key={selectedEvent.id} 
    event={selectedEvent}
    defaultDate={selectedDate}
    whiteboardList={whiteboardList}
    onSave={handleSave}
    onDelete={handleDelete}
    onClose={() => setShowModal(false)}
    prompt={prompt}
    onOpenWhiteboard={(whiteboardId) => {
      setShowModal(false);
      onOpenWhiteboard(whiteboardId);
    }}
  />
)}
{showModal && !selectedEvent && (
  <EventModal
    key="new"
    event={null}
    defaultDate={selectedDate}
    whiteboardList={whiteboardList}
    onSave={handleSave}
    onDelete={handleDelete}
    onClose={() => setShowModal(false)}
    prompt={prompt}
    onOpenWhiteboard={(whiteboardId) => {
      setShowModal(false);
      onOpenWhiteboard(whiteboardId);
    }}
datesSet={(dateInfo) => {
    visibleRangeRef.current = { start: dateInfo.start, end: dateInfo.end };
	loadEvents(dateInfo.start, dateInfo.end);
  }}
  />
)}

    </div>
  );
};

export default Agenda;